import React, { useEffect } from 'react';
import analytics from '../services/analytics';

/**
 * OAuth Callback Page
 * 
 * This page receives OAuth callbacks from the integrations service.
 * It sends the result to the parent window via postMessage and closes itself.
 * 
 * Backend redirects here after OAuth completion:
 * - X: /oauth-callback?code=ABC&state=USER_ID
 * - YouTube/Gmail: /oauth-callback?provider=youtube&user_id=USER_ID&success=true
 * - Errors: /oauth-callback?error=message&provider=PLATFORM
 */
const OAuthCallback: React.FC = () => {
  useEffect(() => {
    const handleCallback = () => {
      try {
        // Parse URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        
        // Extract all possible parameters
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const provider = urlParams.get('provider');
        const userId = urlParams.get('user_id');
        const success = urlParams.get('success');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        console.log('🔵 OAuth Callback received:', {
          code: code ? 'present' : 'missing',
          state,
          provider,
          userId,
          success,
          error
        });

        // Determine platform from provider param or default to 'x' if code is present
        let platform: 'youtube' | 'gmail' | 'x' = 'x';
        if (provider === 'youtube') platform = 'youtube';
        else if (provider === 'gmail') platform = 'gmail';
        else if (provider === 'x' || provider === 'twitter') platform = 'x';

        // Build callback data object
        const callbackData: {
          type: 'oauth_callback';
          platform: 'youtube' | 'gmail' | 'x';
          success: boolean;
          code?: string;
          state?: string;
          userId?: string;
          error?: string;
          errorDescription?: string;
        } = {
          type: 'oauth_callback',
          platform,
          success: !error,
        };

        // Add relevant fields based on callback type
        if (error) {
          callbackData.error = error;
          callbackData.errorDescription = errorDescription || undefined;
        } else if (code && state) {
          // X PKCE flow - has code that needs exchange
          callbackData.code = code;
          callbackData.state = state;
        } else if (userId && success === 'true') {
          // YouTube/Gmail - OAuth already complete on server
          callbackData.userId = userId;
        }

        // Fire a funnel event for social-connect outcomes. Runs inside the popup
        // (which shares localStorage with the parent, so PostHog is initialized
        // and the user is already identified from the opener's session).
        try {
          if (error) {
            analytics.track('social_connection_failed', { provider: platform, error });
          } else {
            analytics.track('social_connection_completed', { provider: platform });
          }
        } catch (e) { /* never break the callback flow */ }

        console.log('🔵 Sending postMessage to parent:', callbackData);

        // Send message to parent window
        if (window.opener) {
          window.opener.postMessage(callbackData, window.location.origin);
          
          // Close this popup after sending message
          setTimeout(() => {
            window.close();
          }, 500);
        } else {
          console.error('❌ No window.opener found - cannot send callback data');
          // If opened as regular page (not popup), redirect to connect socials
          setTimeout(() => {
            window.location.href = '/onboarding/connectSocials';
          }, 2000);
        }
      } catch (error) {
        console.error('❌ Error handling OAuth callback:', error);
        
        // Try to send error to parent
        if (window.opener) {
          window.opener.postMessage(
            {
              type: 'oauth_callback',
              platform: 'x',
              success: false,
              error: 'callback_processing_failed',
              errorDescription: error instanceof Error ? error.message : 'Unknown error',
            },
            window.location.origin
          );
          
          setTimeout(() => {
            window.close();
          }, 500);
        }
      }
    };

    // Run callback handler
    handleCallback();
  }, []);

  return (
    <div style={{
      height: '100vh',
      width: '100vw',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(21, 17, 51, 0.7)',
      fontFamily: 'Manrope, sans-serif'
    }}>
      <div style={{
        textAlign: 'center',
        padding: '40px',
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        maxWidth: '400px'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid rgba(249, 85, 13, 0.2)',
          borderTop: '4px solid #f9550d',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 24px'
        }} />
        <style>
          {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
        </style>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#151133',
          marginBottom: '12px'
        }}>
          Completing Authorization...
        </h2>
        <p style={{
          fontSize: '14px',
          color: '#64748b',
          margin: 0
        }}>
          This window will close automatically
        </p>
      </div>
    </div>
  );
};

export default OAuthCallback;
